import React, { useEffect, useState } from "react";
import { FaHeart, FaRegHeart, FaTrash } from "react-icons/fa";
import "./ProductItem.css";
import { useNavigate } from "react-router-dom";
import { PriceTier } from "../../types/index";
import api from "../../api";
import { SizeQuantity } from '@flooron/shared';

export interface CartSizeItem {
  size: string;
  quantity: number;
  cartItemId: string | number;
}

export interface Product {
  id: string | number;
  name?: string;
  description?: string;
  images?: string[];
  priceTiers?: PriceTier[];
  availableQuantity?: number;
  brand?: string | null;
  favoriteCount?: number;
  cartQuantity?: number | null;
  isFavorite?: boolean;
  orderStatus?: string;
  sizeQuantities?: SizeQuantity[];
  cartSize?: string | null;
  // Cart-specific properties for grouped items
  cartSizes?: CartSizeItem[];
  cartItemId?: string | number;
  cartPrice?: number;
  cartTotal?: number;
}

export type ProductItemType = "category" | "favourites" | "orders" | "search";

interface ProductItemProps {
  product: Product;
  type: ProductItemType;
  onRemove?: (productId: number) => void;
  onQuantityChange?: (productId: number, newQty: number) => void;
  onAddToCart?: (productId: number) => void;
  onToggleFavorite?: (productId: number) => void;
}

const getActivePrice = (item: { priceTiers?: PriceTier[]; cartQuantity?: number }) => {
  const qty = item.cartQuantity ?? 1;
  const tiers = item.priceTiers ?? [];
  if (tiers.length === 0) return 0;
  const matchingTiers = tiers.filter(
    (t) => qty >= t.minQty && (t.maxQty === undefined || qty <= t.maxQty)
  );
  if (matchingTiers.length > 0) {
    return matchingTiers.reduce((prev, curr) =>
      curr.minQty > prev.minQty ? curr : prev
    ).price;
  }
  return tiers.reduce((prev, curr) => (prev.minQty < curr.minQty ? prev : curr)).price;
};

const ProductItem: React.FC<ProductItemProps> = ({
  product,
  type,
  onRemove,
  onQuantityChange,
  onAddToCart,
  onToggleFavorite,
}) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(product.isFavorite ?? false);

  useEffect(() => {
    if (product.isFavorite !== undefined) {
      setIsFavorite(product.isFavorite);
    }
  }, [product.isFavorite]);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.addRemoveFavourite(product.id.toString());
      setIsFavorite(!isFavorite);
      onToggleFavorite?.(Number(product.id));
    } catch (error) {
      console.error("Failed to toggle favorite", error);
    }
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart?.(Number(product.id));
  };

  const handleViewDetail = () => navigate(`/product/${product.id}`);

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === 'favourites') {
      try {
        await api.addRemoveFavourite(product.id.toString());
      } catch (error) {
        console.error("Failed to remove from favorites", error);
      }
    }
    onRemove?.(Number(product.id));
  };

  const price = getActivePrice({ priceTiers: product.priceTiers, cartQuantity: 1 });

  const firstImage =
    product?.images?.[0] && product.images[0] !== ""
      ? product.images[0]
      : "/placeholder-product.png";

  const renderContent = () => {
    switch (type) {
      case "favourites":
        return (
          <div className="product-card-buttons">
              <button className="product-card-add outline" onClick={handleViewDetail}>
                View Details
              </button>
            {/* ✅ Remove from favourites — no heart toggle */}
            <button className="product-card-add remove" onClick={handleRemove}>
              <FaTrash size={14} style={{ marginRight: 6 }} /> Remove
            </button>
          </div>
        );

      case "orders":
        return (
          <>
            {product.orderStatus && (
              <div className="product-card-status">{product.orderStatus}</div>
            )}
            <div className="product-card-buttons">
              <button className="product-card-add outline" onClick={handleViewDetail}>
                View Order Details
              </button>
            </div>
          </>
        );

      case "search":
      case "category":
      default:
        return (
          <div className="product-card-buttons">
            <button className="product-card-add outline" onClick={handleViewDetail}>
              View Details
            </button>
            {/* ✅ React icon heart with filled/outline state */}
            <button
              className={`product-card-like ${isFavorite ? "active" : ""}`}
              onClick={handleToggleFavorite}
              aria-label="Toggle favourite"
            >
              {isFavorite ? <FaHeart size={20} /> : <FaRegHeart size={20} />}
            </button>
          </div>
        );
    }
  };

  return (
    <div className="product-card-container">
      <div className="product-card-images" onClick={handleViewDetail}>
        <img
          src={`https://api.flooron.in/${firstImage}`}
          alt={product?.name || "Product"}
          onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder-product.png"; }}
        />
      </div>
      <div className="product-card-info">
        <p onClick={handleViewDetail}>{product?.brand || "Product"}</p>
        <h1 onClick={handleViewDetail}>{product?.name || "Untitled Product"}</h1>
        <h2>₹{price.toLocaleString("en-IN")}</h2>
        <p className="product-card-desc">{product?.description || "No description available."}</p>
        {renderContent()}
      </div>
    </div>
  );
};

export default ProductItem;